import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, Copy, Trash2, Loader2, Download, BookOpen, Lock } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel, Stat } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { api, formatError, fmtDate, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WebhookPanel } from "@/components/WebhookPanel";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const saveBlob = (data, name, type) => {
  const url = URL.createObjectURL(new Blob([data], { type }));
  Object.assign(document.createElement("a"), { href: url, download: name }).click();
  URL.revokeObjectURL(url);
};

const NewKey = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [raw, setRaw] = useState("");
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/account/api-keys", { name });
      setRaw(data.key);
      setName("");
      onCreated();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <form onSubmit={create} className="flex flex-wrap gap-3" data-testid="api-key-create-form">
        <Input data-testid="api-key-name-input" required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name, e.g. Billing system (production)" className="h-11 flex-1 min-w-[220px]" />
        <button data-testid="api-key-create-btn" type="submit" disabled={busy} className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Create key
        </button>
      </form>
      {raw && (
        <div data-testid="api-key-reveal" className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Copy this key now. For your security it will not be shown again.</p>
          <div className="mt-2 flex items-center gap-2">
            <code data-testid="api-key-value" className="flex-1 font-mono text-xs break-all bg-white dark:bg-slate-950 rounded-lg px-3 py-2 border border-amber-200 dark:border-amber-900">{raw}</code>
            <button data-testid="api-key-copy-btn" onClick={() => { navigator.clipboard?.writeText(raw); toast.success("Key copied"); }} className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900"><Copy size={14} /></button>
          </div>
          <button data-testid="api-key-dismiss-btn" onClick={() => setRaw("")} className="mt-3 text-xs underline">I've stored it securely</button>
        </div>
      )}
    </div>
  );
};

const KeyList = ({ keys, onChange }) => {
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Systems using it will stop working immediately.`)) return;
    try {
      await api.delete(`/account/api-keys/${k.id}`);
      toast.success("Key revoked");
      onChange();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  if (!keys.length) return <p className="text-sm text-slate-500" data-testid="api-keys-empty">No keys yet.</p>;
  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="api-keys-list">
      {keys.map((k) => (
        <div key={k.id} data-testid={`api-key-row-${k.id.slice(0, 8)}`} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div>
            <p className="text-sm font-semibold">{k.name} {k.revoked_at && <span className="ml-2 text-[11px] uppercase tracking-wider text-red-600">Revoked</span>}</p>
            <p className="text-xs text-slate-500 font-mono">{k.prefix}… · created {fmtDate(k.created_at)} · last used {fmtDate(k.last_used_at)}</p>
          </div>
          {!k.revoked_at && <button data-testid={`api-key-revoke-${k.id.slice(0, 8)}`} onClick={() => revoke(k)} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 dark:hover:bg-red-950/40"><Trash2 size={13} /> Revoke</button>}
        </div>
      ))}
    </div>
  );
};

const Usage = () => {
  const [month, setMonth] = useState(thisMonth());
  const [u, setU] = useState(null);
  useEffect(() => { api.get("/account/usage", { params: { month } }).then((r) => setU(r.data)).catch(() => setU(null)); }, [month]);
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/account/usage.csv", { params: { month }, responseType: "blob" });
      saveBlob(data, `PostalCloud-API-usage-${month}.csv`, "text/csv");
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="usage-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm" />
        <button data-testid="usage-export-btn" onClick={exportCsv} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><Download size={14} /> Usage statement (CSV)</button>
      </div>
      <div className="grid grid-cols-2 gap-4 max-w-lg">
        <Stat testId="usage-count" label="API letters this month" value={u?.count ?? "—"} />
        <Stat testId="usage-total" label="Amount due (VAT incl.)" value={u ? gbp(u.total_gbp) : "—"} accent />
      </div>
      <p className="text-xs text-slate-500">API sends are billed monthly by invoice at your agreed per-letter rate. Card-paid bulk batches are not included here.</p>
    </div>
  );
};

export default function ApiKeys() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const load = () => api.get("/account/api-keys").then((r) => setKeys(r.data));
  useEffect(() => { if (user?.enterprise && (user.org?.perms || []).includes("keys:manage")) load(); }, [user?.enterprise]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user?.enterprise || !(user.org?.perms || []).includes("keys:manage")) {
    return (
      <div data-testid="api-locked" className="max-w-xl">
        <PageHeader eyebrow="Enterprise" title="API & usage" />
        <Panel><p className="flex gap-2 text-sm"><Lock size={16} className="text-emerald-600 shrink-0" /> The PostalCloud API is available to approved Enterprise accounts.</p>
          <Link to="/enterprise#enquire" className="inline-flex mt-4 underline text-sm">Request Enterprise access</Link></Panel>
      </div>
    );
  }
  return (
    <div data-testid="api-page" className="max-w-4xl space-y-6">
      <PageHeader eyebrow="Enterprise" title="API & usage">
        <Link to="/developers" data-testid="api-docs-link" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><BookOpen size={15} /> API documentation</Link>
      </PageHeader>
      <Panel title="API keys">
        <p className="text-sm text-slate-500 mb-4">Keys give full sending access to your account. Keep them on your servers only, never in browsers or mobile apps. Every API call is recorded in your audit trail.</p>
        <NewKey onCreated={load} />
        <div className="mt-6"><KeyList keys={keys} onChange={load} /></div>
      </Panel>
      <Panel title="Webhooks" testId="api-webhook">
        <p className="text-sm text-slate-500 mb-4">Get an instant signed POST when an ID check is decided, a letter is claimed, or a letter bounces back. <Link to="/developers#webhooks" className="underline">How to verify signatures</Link>.</p>
        <WebhookPanel />
      </Panel>
      <Panel title="Monthly usage"><Usage /></Panel>
    </div>
  );
}
